import { useEffect, useState } from "react";
import { ActivityIndicator, View as NativeView, Platform, Pressable, type ViewStyle } from "react-native";
import { useRouter } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Icon } from "@visioni/a3s-ui/components";
import { useCombobox } from "@visioni/a3s-ui/hooks";
import { Text, View } from "@src/components";
import { useTenantId } from "@src/features/tenants";
import { formatCalendarDate } from "@src/utils/format-date";
import { useRoadSenseWorkbookDownload } from "../hooks/useRoadSenseWorkbookDownload";

export interface ExportMenuProps {
  readonly reportId: string;
  readonly siteId: string;
  /** This site's `.xlsx` key; without one there is nothing to download. */
  readonly workbookS3Key: string | undefined;
  /** The last day the stored workbook covers, shown under "Download workbook". */
  readonly workbookPeriodEnd: string | undefined;
}

type ExportItem = "download" | "email";

const getItemId = (item: ExportItem): string => item;

/**
 * The site report's "Export" menu: download the workbook, or go to the site's
 * report emails. Follows a3s-ui's `PaginationPageSizeSelect`.
 *
 * The download presigns on choice (see `useRoadSenseWorkbookDownload`), so its
 * loading and error lines sit under the trigger once the menu has closed. The
 * workbook only covers up to the last daily run, which the "(to …)" line names.
 */
export function ExportMenu({ reportId, siteId, workbookS3Key, workbookPeriodEnd }: ExportMenuProps) {
  const { theme } = useUnistyles();
  const router = useRouter();
  const tenantId = useTenantId();
  const { download, isDownloading, error } = useRoadSenseWorkbookDownload(reportId, workbookS3Key);
  const [hovered, setHovered] = useState(false);

  const items: readonly ExportItem[] = workbookS3Key !== undefined ? ["download", "email"] : ["email"];
  const coversTo = workbookPeriodEnd !== undefined ? formatCalendarDate(workbookPeriodEnd) : "";

  const { isOpen, open, close, containerRef, highlightedIndex, setHighlightedIndex, getOptionProps } =
    useCombobox<ExportItem>({
      options: items,
      getOptionId: getItemId,
      onSelect: (item) => {
        if (item === "download") download();
        else {
          router.push({
            pathname: "/tenants/[tenantId]/reports/roadsense/report/[reportId]/site/[siteId]/email-delivery",
            params: { tenantId, reportId, siteId },
          });
        }
      },
    });

  // `useCombobox` closes on an outside click; Escape is this menu's own.
  useEffect(() => {
    if (!isOpen || Platform.OS !== "web") return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, close]);

  return (
    <View gap="xs" alignItems="flex-end" style={styles.raised}>
      <NativeView ref={containerRef} style={styles.anchor}>
        <Pressable
          onPress={isOpen ? close : open}
          onHoverIn={() => setHovered(true)}
          onHoverOut={() => setHovered(false)}
          accessibilityRole="button"
          accessibilityLabel="Export"
          accessibilityState={{ expanded: isOpen }}
          // RN-web maps this to the DOM; native has no equivalent.
          aria-haspopup="menu"
          style={[
            styles.trigger,
            (hovered || isOpen) && styles.triggerActive,
            Platform.OS === "web" && styles.webInteractive,
          ]}
        >
          <Text textStyle="xs/semibold" color="text">
            Export
          </Text>
          <Icon name={isOpen ? "chevronUp" : "chevronDown"} size={14} color={theme.colors.icon} />
        </Pressable>

        {isOpen && (
          <>
            {/* Native has no global hit-test, so the tap-away target has to
                exist in the tree. On web `useCombobox` handles it on the document. */}
            {Platform.OS !== "web" && <Pressable style={styles.backdrop} onPress={close} />}
            <NativeView style={styles.menu} accessibilityRole="menu">
              {items.map((item, index) => {
                const itemProps = getOptionProps(item, index);
                return (
                  <Pressable
                    key={item}
                    onPress={itemProps.onPress}
                    onHoverIn={() => setHighlightedIndex(index)}
                    accessibilityRole="menuitem"
                    style={[
                      styles.item,
                      index === highlightedIndex && styles.itemHighlighted,
                      Platform.OS === "web" && styles.webInteractive,
                    ]}
                  >
                    {item === "download" ?
                      <>
                        <Text textStyle="sm/regular" color="text">
                          Download workbook
                        </Text>
                        {coversTo ?
                          <Text textStyle="xs/regular" color="textMuted">
                            (to {coversTo})
                          </Text>
                        : null}
                      </>
                    : <Text textStyle="sm/regular" color="text">
                        Email Reports
                      </Text>
                    }
                  </Pressable>
                );
              })}
            </NativeView>
          </>
        )}
      </NativeView>
      {isDownloading ?
        <View flexDirection="row" gap="sm" alignItems="center">
          <ActivityIndicator />
          <Text textStyle="xs/regular" color="textMuted">
            Preparing download…
          </Text>
        </View>
      : null}
      {error && !isDownloading ?
        <Text textStyle="xs/regular" color="textMuted">
          Download failed. Please try again.
        </Text>
      : null}
    </View>
  );
}

const MENU_OFFSET = 4;
const TRIGGER_HEIGHT = 36;

const styles = StyleSheet.create((theme) => ({
  // RN-web gives every View its own stacking context, so the menu only draws
  // over the map and chart below when its wrapper is raised too.
  raised: {
    position: "relative",
    zIndex: theme.zIndex.inputOpen,
  },
  anchor: {
    position: "relative",
  },
  trigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xs,
    height: TRIGGER_HEIGHT,
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.lg,
    borderRadius: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    backgroundColor: theme.colors.surface,
  },
  triggerActive: {
    borderColor: theme.colors.field.border_default,
  },
  backdrop: {
    position: "absolute",
    top: -1000,
    left: -1000,
    right: -1000,
    bottom: -1000,
    zIndex: theme.zIndex.backdrop,
  },
  menu: {
    position: "absolute",
    top: TRIGGER_HEIGHT + MENU_OFFSET,
    right: 0,
    minWidth: 200,
    paddingVertical: theme.spacing.xs,
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.md,
    overflow: "hidden",
    ...theme.shadows.sm,
    zIndex: theme.zIndex.dropdown,
  },
  item: {
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xl,
    alignItems: "flex-start",
  },
  itemHighlighted: {
    backgroundColor: theme.colors.surface2.hover,
  },
  webInteractive: Object.assign({ cursor: "pointer" } satisfies ViewStyle, {
    transition: "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out",
  }),
}));
